import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FiMapPin, FiCalendar, FiEdit2 } from 'react-icons/fi';
import { useApi } from '../useApi';
import TeeSelectionModal from './TeeSelectionModal';
import CourseSections from './CourseSections';
import { useNearbyCourses } from '../hooks/useNearbyCourses';
import { courseMeta, localIsoDate } from '../utils/courseDistance';

// compact: single-screen variant (advanced wizard Basics) — no header/subtitle
// and the fields run Name, Date, Course so everything fits a phone viewport.
// onCourseAdopted: a directory course was added from the picker; the parent
// owns `allCourses`, so it is told to add it to the list.
// membersMayAddCourses: offer members the directory too (quick rounds only —
// see useNearbyCourses); course managers are offered it regardless.
const Step1_QuickSetup = ({ data, updateData, allCourses, isLocked, isEditMode, tournamentId, compact = false, onCourseAdopted, membersMayAddCourses = false }) => {
    const [existingNames, setExistingNames] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [isListVisible, setIsListVisible] = useState(false);
    const [teeModalCourse, setTeeModalCourse] = useState(null);
    // Editing an existing tournament keeps its name — auto-naming is only for
    // fresh setups (otherwise changing course/date would rename it).
    const [isNameAutoGenerated, setIsNameAutoGenerated] = useState(!isEditMode);
    const [showNameEditor, setShowNameEditor] = useState(false);
    const searchRef = useRef(null);
    const { authenticatedFetch } = useApi();
    const lastSavedNameRef = useRef(data.name);

    const handleNameBlur = useCallback(async (e) => {
        const newName = e.target.value;
        if (!isEditMode || !tournamentId) return;
        if (newName === lastSavedNameRef.current) return;

        try {
            await authenticatedFetch(`/tournaments/${tournamentId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newName }),
            });
            lastSavedNameRef.current = newName;
        } catch (error) {
            console.error('Failed to auto-save tournament name:', error);
        }
    }, [isEditMode, tournamentId, authenticatedFetch]);

    const generateUniqueName = (baseName) => {
        if (!existingNames.has(baseName)) return baseName;
        let counter = 2;
        let newName = `${baseName} (${counter})`;
        while (existingNames.has(newName)) {
            counter++;
            newName = `${baseName} (${counter})`;
        }
        return newName;
    };

    useEffect(() => {
        // Compact (advanced) mode names itself up front — never auto-derive the
        // name from course + date the way the quick round does.
        if (compact || !isNameAutoGenerated || isLocked) return;
        const selectedCourse = data.courses?.[0];
        if (selectedCourse && data.date) {
            const dateParts = data.date.split('-');
            const dateStr = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}` : '';
            const baseName = `${selectedCourse.name} - ${dateStr}`;
            if (data.name !== baseName) {
                const uniqueName = generateUniqueName(baseName);
                updateData({ name: uniqueName });
            }
        }
    }, [data.date, data.courses, isNameAutoGenerated, isLocked]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchTournaments = async () => {
            try {
                const response = await authenticatedFetch('/tournaments');
                if (response.ok) {
                    const tournaments = await response.json();
                    setExistingNames(new Set(tournaments.map(t => t.name)));
                }
            } catch (error) {
                console.error("Failed to fetch tournaments for name check", error);
            }
        };
        fetchTournaments();
    }, [authenticatedFetch]);

    useEffect(() => {
        if (!data.date) {
            // LOCAL date. toISOString is UTC, so for the first hour of a BST
            // day the default was yesterday — and the wizard's "is this round
            // today?" check (which starts it on finish) reads local time.
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            updateData({ date: today });
        }
        if (!data.format) {
            updateData({ format: 'Stableford', matchplay_percentage: 95 });
        }
        if (data.courses && data.courses.length > 0) {
            const selectedCourse = allCourses.find(c => c.id === data.courses[0].id);
            if (selectedCourse) {
                setSearchTerm(selectedCourse.name);
            }
        }
    }, [allCourses]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsListVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNameInput = (e) => {
        if (isLocked) return;
        setIsNameAutoGenerated(false);
        updateData({ name: e.target.value });
    };

    const handleDateChange = (e) => {
        if (isLocked) return;
        updateData({ date: e.target.value });
    };

    const handleSearchChange = (e) => {
        if (isLocked) return;
        setSearchTerm(e.target.value);
        setIsListVisible(true);
        if (e.target.value === '') {
             updateData({ courses: [] });
        }
    };

    const handleCourseSelect = (course) => {
        if (isLocked) return;
        if (course.tees && course.tees.length > 0) {
            setIsListVisible(false);
            setTeeModalCourse(course);
        } else {
            confirmCourseWithTee(course, null);
        }
    };

    const handleTeeSelect = (tee, teesInPlay) => {
        if (teeModalCourse) {
            confirmCourseWithTee(teeModalCourse, tee, teesInPlay);
        }
        setTeeModalCourse(null);
    };

    // `teesInPlay` is the set of tees declared for the round — more than one
    // makes it a mixed-tee day, and becomes the menu the per-player pickers
    // offer when groups are assigned.
    const confirmCourseWithTee = (course, tee, teesInPlay = []) => {
        setSearchTerm(course.name);
        setIsListVisible(false);
        const teeIds = teesInPlay?.length ? teesInPlay : (tee?.id ? [tee.id] : []);
        updateData({
            courses: [{
                ...course, sequence_number: 1,
                tee_id: tee?.id || null, tee_name: tee?.name || null,
                tees_in_play: teeIds,
            }]
        });
    };

    // Near the phone first, then the courses this society actually plays —
    // see utils/courseDistance and utils/courseOrder. Alphabetical is the order
    // of a directory, not of a decision.
    const { sections, near, miles, adoptDialog } = useNearbyCourses({
        courses: allCourses,
        search: searchTerm,
        today: data.date === localIsoDate(),
        membersMayAdd: membersMayAddCourses,
        // A directory course just added to the society goes straight on to
        // its tee, exactly as if it had been in the list all along.
        onAdopted: (course) => {
            onCourseAdopted?.(course);
            handleCourseSelect(course);
        },
    });
    const hasRows = sections.searching
        ? sections.rows.length > 0
        : sections.nearby.length + sections.favourites.length + sections.rest.length > 0;

    const formatHumanDate = (dateStr) => {
        if (!dateStr) return 'Select Date';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const hasCourse = (data.courses?.length || 0) > 0;

    const courseField = (
        <div className="wiz-field" ref={searchRef} style={{ position: 'relative' }}>
            <label htmlFor="course-search" className="wiz-label"><FiMapPin /> Course</label>
            <div className="wiz-search">
                <svg className="wiz-search__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
                </svg>
                <input
                    type="text"
                    id="course-search"
                    className="wiz-input wiz-input--search"
                    placeholder="Search for a course..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => !isLocked && setIsListVisible(true)}
                    autoComplete="off"
                    disabled={isLocked}
                />
            </div>
            {hasCourse && (
                <span className="wiz-tee-tag">
                    {(data.courses[0].tees_in_play || []).length > 1
                        ? `Tees: ${(data.courses[0].tees_in_play || [])
                            .map(id => (data.courses[0].tees || []).find(t => t.id === id)?.name)
                            .filter(Boolean).join(', ')}`
                        : `Tee: ${data.courses[0].tee_name || 'Default'}`}
                </span>
            )}
            {isListVisible && !isLocked && (
                <div className="wiz-course-list">
                    {hasRows ? (
                        <CourseSections
                            sections={sections}
                            near={near}
                            renderRow={(entry) => {
                                // Why this one is where it is. Only where there
                                // is something to say: "0 times" under a course
                                // reads as a warning against it.
                                const meta = courseMeta(entry, { miles });
                                return (
                                    <div key={entry.course.id} className="wiz-course-list__row"
                                        onClick={() => handleCourseSelect(entry.course)}>
                                        <span className="wiz-course-list__name">{entry.course.name}</span>
                                        {meta && <span className="wiz-course-list__meta">{meta}</span>}
                                    </div>
                                );
                            }}
                        />
                    ) : (
                        <div className="wiz-course-list__empty">No courses found</div>
                    )}
                </div>
            )}
        </div>
    );

    const dateField = (
        <div className="wiz-field">
            <label htmlFor="date" className="wiz-label"><FiCalendar /> Date</label>
            <div className="wiz-date">
                <input type="date" id="date" name="date" value={data.date || ''}
                    onChange={handleDateChange} className="wiz-date__native"
                    disabled={isLocked} />
                <span className={`wiz-date__overlay ${isLocked ? 'wiz-date__overlay--disabled' : ''}`}>
                    {formatHumanDate(data.date)}
                </span>
            </div>
        </div>
    );

    // Advanced comps name themselves up front: a plain, always-visible name
    // field is the first thing entered — no course-gating, no quick-round
    // "we'll call it" auto-default.
    const nameField = (
        <div className="wiz-field">
            <label htmlFor="comp-name" className="wiz-label"><FiEdit2 /> Competition name</label>
            <input
                type="text"
                id="comp-name"
                className="wiz-input"
                placeholder="Name this competition"
                value={data.name || ''}
                onChange={handleNameInput}
                onBlur={handleNameBlur}
                disabled={isLocked}
            />
        </div>
    );

    const nameChip = hasCourse && data.date && (
        <div className="wiz-name-chip">
            {showNameEditor ? (
                <input
                    type="text"
                    className="wiz-input wiz-input--inline"
                    value={data.name}
                    onChange={handleNameInput}
                    onBlur={(e) => { handleNameBlur(e); setShowNameEditor(false); }}
                    autoFocus
                    disabled={isLocked}
                />
            ) : (
                <>
                    <span className="wiz-name-chip__label">We'll call it:</span>
                    <span
                        className="wiz-name-chip__value"
                        onClick={() => !isLocked && setShowNameEditor(true)}
                    >{data.name || '—'}</span>
                    {!isLocked && (
                        <button
                            type="button"
                            className="wiz-name-chip__rename"
                            onClick={() => setShowNameEditor(true)}
                            aria-label="Edit name"
                        >
                            <FiEdit2 size={14} />
                        </button>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className={`wiz-step${compact ? ' wiz-step--compact' : ''}`}>
            {!compact && (
                <>
                    <h2 className="wiz-header">Where are you playing?</h2>
                    <p className="wiz-subtitle">Pick a course and date — we'll name the round for you.</p>
                </>
            )}

            {isLocked && (
                <div className="wiz-locked-banner">
                    Tournament started — details are locked
                </div>
            )}

            {compact ? <>{nameField}{dateField}{courseField}</> : <>{courseField}{dateField}{nameChip}</>}

            <TeeSelectionModal
                isOpen={!!teeModalCourse}
                course={teeModalCourse}
                onSelect={handleTeeSelect}
                onClose={() => setTeeModalCourse(null)}
            />
            {adoptDialog}
        </div>
    );
};

export default Step1_QuickSetup;
